import * as React from 'react';
import { Award, Medal, Trophy } from 'lucide-react';
import { ScrollArea } from './Avatar';
import { Card, CardContent, CardHeader, CardTitle, Surface } from './Card';
import { Center, Inline, Stack } from './Layout';
import { Loading } from './Spinner';
import { Text } from './Text';

export interface DriverLeaderboardEntry {
  id: string;
  name: string;
  rank: number;
  completedCalls: number;
}

export interface DriverLeaderboardViewProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  entries: DriverLeaderboardEntry[];
  /** The driver whose page this is; their row is picked out. */
  currentId?: string;
  loading?: boolean;
  title?: React.ReactNode;
}

/* A trophy, a medal and a ribbon for the podium; a number for everyone else. */
function RankIcon({ rank }: { rank: number }) {
  switch (rank) {
    case 1:
      return <Trophy className="size-icon-md text-podium-gold" />;
    case 2:
      return <Medal className="size-icon-md text-podium-silver" />;
    case 3:
      return <Award className="size-icon-md text-podium-bronze" />;
    default:
      return (
        <Inline
          as="span"
          gap={0}
          align="center"
          justify="center"
          className="size-5"
        >
          <Text as="span" variant="body-sm-strong" tone="muted">
            {rank}
          </Text>
        </Inline>
      );
  }
}

/**
 * Drivers ranked by completed calls, the current one highlighted.
 *
 *   <DriverLeaderboardView entries={entries} currentId={driver.id} />
 *
 * The entries arrive ranked; the view only draws them. Loading and empty
 * each get the same card so the page does not jump when the data lands.
 */
export const DriverLeaderboardView = React.forwardRef<
  HTMLDivElement,
  DriverLeaderboardViewProps
>(function DriverLeaderboardView(
  {
    entries,
    currentId,
    loading = false,
    title = 'Driver Leaderboard',
    ...props
  },
  ref
) {
  const currentRank = entries.find((e) => e.id === currentId)?.rank;

  const heading = (
    <Inline gap={2} align="center">
      <Trophy className="size-icon-md text-podium-gold" />
      <CardTitle>{title}</CardTitle>
    </Inline>
  );

  if (loading) {
    return (
      <Card ref={ref} {...props}>
        <CardHeader>{heading}</CardHeader>
        <CardContent className="h-[250px]">
          <Loading fill="parent" pad="none" title="Loading leaderboard..." />
        </CardContent>
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <Card ref={ref} {...props}>
        <CardHeader>{heading}</CardHeader>
        <CardContent className="h-[250px]">
          <Center fill="parent">
            <Text variant="body-sm" tone="muted">
              No data available
            </Text>
          </Center>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card ref={ref} {...props}>
      <CardHeader>
        <Inline gap={0} align="center" justify="between">
          {heading}
          {currentRank ? (
            <Text as="span" variant="body-sm" tone="muted">
              Current rank:{' '}
              <Text as="span" variant="body-sm-strong" tone="default">
                #{currentRank}
              </Text>
            </Text>
          ) : null}
        </Inline>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[250px]">
          <Stack gap={2}>
            {entries.map((entry) => {
              const isCurrent = entry.id === currentId;
              const isPodium = entry.rank <= 3;
              return (
                <Surface
                  key={entry.id}
                  radius="lg"
                  padding="sm"
                  bordered={isCurrent}
                  tone={isCurrent ? 'info' : isPodium ? 'muted' : 'none'}
                  aria-current={isCurrent || undefined}
                >
                  <Inline gap={3} align="center" justify="between">
                    <Inline gap={3} align="center">
                      <RankIcon rank={entry.rank} />
                      <Text
                        as="span"
                        variant="body-sm-strong"
                        tone={isCurrent ? 'inherit' : 'default'}
                        truncate
                        className="max-w-[150px]"
                      >
                        {entry.name}
                      </Text>
                    </Inline>
                    <Inline gap={2} align="center">
                      <Text
                        as="span"
                        variant="body-sm-strong"
                        tone={isCurrent ? 'inherit' : 'default'}
                        numeric
                      >
                        {entry.completedCalls}
                      </Text>
                      <Text as="span" variant="caption" tone="muted">
                        calls
                      </Text>
                    </Inline>
                  </Inline>
                </Surface>
              );
            })}
          </Stack>
        </ScrollArea>
      </CardContent>
    </Card>
  );
});
